/* eslint-disable react/jsx-no-bind */
import { Settings, Search, Refresh } from '@mui/icons-material';
import {
  Box,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem as DropdownItem,
  Paper,
  IconButton,
  Menu,
  CardContent,
  Card,
  TablePagination,
  useTheme,
  Divider,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Chip,
  Typography,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import NotFound from '../../assets/not_found.png';
import { tokens } from '../../theme';
import { formatToIST, formatToISTDate } from '../../utils/dateFormatter';
import logger from '../../utils/logger';

import bookingService from './bookingService';

import 'react-quill/dist/quill.snow.css';

import './index.scss';

const Booking = () => {
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  const [bookingIdFilter, setBookingIdFilter] = useState('');
  const [mobileFilter, setMobileFilter] = useState('');
  const [tripIdFilter, setTripIdFilter] = useState('');
  const [shipmentIdFilter, setShipmentIdFilter] = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [deliveryFilter, setDeliveryFilter] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    // { value: 'CRT', label: 'Created' },
    { value: 'ASG', label: 'Assigned' },
    { value: 'INT', label: 'In Transit' },
    { value: 'DLD', label: 'Delivered' },
    // { value: 'CLD', label: 'Canceled' },
    { value: 'RFP', label: 'Ready for Pickup' },
    // { value: 'ICR', label: 'Incident Reported' },
    // { value: 'NTY', label: 'Notify' },
  ];


  const getStatusStyles = (status) => {
    if (!status) return 'default';
    switch (status.toLowerCase()) {
      case 'delivered':
        return 'success';
      case 'in transit':
        return 'warning';
        // case 'created':
        // return 'info';
      // case 'canceled':
        // return 'error';
      case 'ready for pickup':
        return 'secondary';
      case 'assigned':
        return 'primary';
      case 'pickup':
        return 'info';
        // case 'incident reported':
        // return 'error';
      // case 'notify':
        // return 'info';
      default:
        return 'default';
    }
  };

  const formatDateBasedOnStatus = (date, status) => {
    if (!date || date === 'N/A' || date === null || date === undefined) {
      return 'N/A';
    }

    // Handle different date formats
    let dateToFormat = date;

    // If date is already in expected format, use it directly
    if (typeof date === 'string' && date.includes('-')) {
      dateToFormat = date;
    }

    switch (status) {
      case 'Created':
      case 'Assigned':
      case 'In Transit':
      case 'Pickup':
      case 'Delivered':
        return formatToISTDateTime(dateToFormat);
      case 'Canceled':
        return formatToISTDate(dateToFormat);
      default:
        return formatToISTDateTime(dateToFormat);
    }
  };

  const getDeliveryStatus = (booking) => {
    const now = dayjs();
    const expectedDelivery = dayjs(booking.estimatedDeliveryDate);
    const actualDelivery =
      booking.status === 'Delivered' ? dayjs(booking.bookingDateTime) : null;

    if (booking.status === 'Delivered') {
      return actualDelivery && actualDelivery.isAfter(expectedDelivery)
        ? 'exceeded'
        : 'on time';
    }

    if (now.isAfter(expectedDelivery)) {
      return 'exceeded';
    }

    return 'pending';
  };

  const getRowBackgroundColor = (booking) => {
    const statusCode = booking?.statusCode?.toUpperCase();
    switch (statusCode) {
      case 'ASG':
        return '#e1f5fe'; // Light cyan for assigned
      case 'INT':
        return '#fff3e0'; // Light orange for in transit
      case 'DLD':
        return '#e8f5e9'; // Light green for delivered
      case 'RFP':
        return '#f3e5f5'; // Light purple for ready for pickup
      default:
    }


    // Fall back to delivery status for other statuses
    const deliveryStatus = getDeliveryStatus(booking);
    switch (deliveryStatus) {
      case 'pending':
        return '#e3f2fd'; // Light blue
      case 'success':
        return '#e8f5e8'; // Light green
      case 'exceeded':
        // return '#ffebee'; // Light red
        return 'transparent';
      default:
        return 'transparent';
    }
  };

  const fetchBookings = async (opt = {}) => {
    setIsLoading(true);
    try {
      const apiPage = (opt.page ?? page) + 1;
      const apiPageSize = opt.pageSize ?? rowsPerPage;
      const searchTerm = opt.search ?? search;
      const bookingId = opt.bookingId ?? bookingIdFilter;
      const mobile = opt.mobile ?? mobileFilter;
      const tripId = opt.tripId ?? tripIdFilter;
      const shipmentId = opt.shipmentId ?? shipmentIdFilter;
      const status = opt.status ?? statusFilter;
      const deliveryStatusFilter = opt.deliveryFilter ?? deliveryFilter;
      const start =
        opt.startDate ??
        (startDate ? dayjs(startDate).format('YYYY-MM-DD') : null);
      const end =
        opt.endDate ?? (endDate ? dayjs(endDate).format('YYYY-MM-DD') : null);

      const { rows, total } = await bookingService.fetchBookings({
        page: apiPage,
        pageSize: apiPageSize,
        search: searchTerm || '',
        bookingId: bookingId || '',
        mobile: mobile || '',
        tripId: tripId || '',
        shipmentId: shipmentId || '',
        status: status || '',
        startDate: start,
        endDate: end,
        deliveryFilter: deliveryStatusFilter || '',
      });

      setData(rows);
      setTotalCount(total);
    } catch (error) {
      logger.error('Error fetching bookings:', error);
      setData([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings({ page: 0, pageSize: 5 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    setPage(0);
    const formattedStartDate =
      startDate && dayjs(startDate).isValid()
        ? dayjs(startDate).format('YYYY-MM-DD')
        : null;
    const formattedEndDate =
      endDate && dayjs(endDate).isValid()
        ? dayjs(endDate).format('YYYY-MM-DD')
        : null;
    fetchBookings({
      page: 0,
      pageSize: rowsPerPage,
      search: search || '',
      bookingId: bookingIdFilter || '',
      mobile: mobileFilter || '',
      tripId: tripIdFilter || '',
      shipmentId: shipmentIdFilter || '',
      status: statusFilter || '',
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      deliveryFilter: deliveryFilter || '',
    });
  };

  const handleReset = () => {
    setSearch('');
    setBookingIdFilter('');
    setMobileFilter('');
    setTripIdFilter('');
    setShipmentIdFilter('');
    setStatusFilter('');
    setDeliveryFilter('');
    setStartDate(null);
    setEndDate(null);
    setPage(0);
    setRowsPerPage(5);

    fetchBookings({
      page: 0,
      pageSize: 5,
      search: '',
      bookingId: '',
      mobile: '',
      tripId: '',
      shipmentId: '',
      status: '',
      startDate: null,
      endDate: null,
      deliveryFilter: '',
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    const formattedStartDate =
      startDate && dayjs(startDate).isValid()
        ? dayjs(startDate).format('YYYY-MM-DD')
        : null;
    const formattedEndDate =
      endDate && dayjs(endDate).isValid()
        ? dayjs(endDate).format('YYYY-MM-DD')
        : null;
    fetchBookings({
      page: newPage,
      pageSize: rowsPerPage,
      search: search || '',
      bookingId: bookingIdFilter || '',
      mobile: mobileFilter || '',
      tripId: tripIdFilter || '',
      shipmentId: shipmentIdFilter || '',
      status: statusFilter || '',
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      deliveryFilter: deliveryFilter || '',
    });
  };

  const handleChangeRowsPerPage = (event) => {
    const newSize = parseInt(event.target.value, 10);
    setRowsPerPage(newSize);
    setPage(0);
    const formattedStartDate =
      startDate && dayjs(startDate).isValid()
        ? dayjs(startDate).format('YYYY-MM-DD')
        : null;
    const formattedEndDate =
      endDate && dayjs(endDate).isValid()
        ? dayjs(endDate).format('YYYY-MM-DD')
        : null;
    fetchBookings({
      page: 0,
      pageSize: newSize,
      search: search || '',
      bookingId: bookingIdFilter || '',
      mobile: mobileFilter || '',
      tripId: tripIdFilter || '',
      shipmentId: shipmentIdFilter || '',
      status: statusFilter || '',
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      deliveryFilter: deliveryFilter || '',
    });
  };

  const handleMenuOpen = (event, row) => {
    setMenuAnchor(event.currentTarget);
    setSelectedBooking(row);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleViewDetails = (booking, Id) => {
    navigate(`/booking/${Id}`, { state: { booking } });
  };

  return (
    <Box sx={{ padding: 2, paddingLeft: 4 }}>
      <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="h5">Bookings</Typography>
          </Box>
          <Divider sx={{ mb: 3 }} />
          <Box>
            {/* Filter Section - Compact Single Line */}
            <Box
              sx={{
                display: 'flex',
                gap: 1.5,
                mb: 2,
                alignItems: 'center',
                overflowX: 'hidden',
                overflowY: 'hidden',
                minWidth: 'fit-content',
                maxWidth: '100%',
                padding: '12px 16px',
                backgroundColor: '#f8f9fa',
                borderRadius: '12px',
                border: '1px solid #e9ecef',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                flexWrap: 'wrap',
              }}
            >
              <TextField
                label="Booking ID"
                variant="outlined"
                size="small"
                value={bookingIdFilter}
                onChange={(e) => setBookingIdFilter(e.target.value)}
                sx={{
                  minWidth: 110,
                  '& .MuiOutlinedInput-root': {
                    height: '36px',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    '&.Mui-focused fieldset': {
                      borderColor: colors.swiftcarry[100],
                      borderWidth: '2px',
                    },
                    '&:hover fieldset': {
                      borderColor: colors.swiftcarry[100],
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '13px',
                    '&.Mui-focused': {
                      color: colors.swiftcarry[100],
                    },
                  },
                }}
              />
              <TextField
                label="Mobile"
                variant="outlined"
                size="small"
                value={mobileFilter}
                onChange={(e) => setMobileFilter(e.target.value)}
                sx={{
                  minWidth: 100,
                  '& .MuiOutlinedInput-root': {
                    height: '36px',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    '&.Mui-focused fieldset': {
                      borderColor: colors.swiftcarry[100],
                      borderWidth: '2px',
                    },
                    '&:hover fieldset': {
                      borderColor: colors.swiftcarry[100],
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '13px',
                    '&.Mui-focused': {
                      color: colors.swiftcarry[100],
                    },
                  },
                }}
              />
              <TextField
                label="Trip ID"
                variant="outlined"
                size="small"
                value={tripIdFilter}
                onChange={(e) => setTripIdFilter(e.target.value)}
                sx={{
                  minWidth: 110,
                  '& .MuiOutlinedInput-root': {
                    height: '36px',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    '&.Mui-focused fieldset': {
                      borderColor: colors.swiftcarry[100],
                      borderWidth: '2px',
                    },
                    '&:hover fieldset': {
                      borderColor: colors.swiftcarry[100],
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '13px',
                    '&.Mui-focused': {
                      color: colors.swiftcarry[100],
                    },
                  },
                }}
              />
              <TextField
                label="Shipment ID"
                variant="outlined"
                size="small"
                value={shipmentIdFilter}
                onChange={(e) => setShipmentIdFilter(e.target.value)}
                sx={{
                  minWidth: 110,
                  '& .MuiOutlinedInput-root': {
                    height: '36px',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    '&.Mui-focused fieldset': {
                      borderColor: colors.swiftcarry[100],
                      borderWidth: '2px',
                    },
                    '&:hover fieldset': {
                      borderColor: colors.swiftcarry[100],
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '13px',
                    '&.Mui-focused': {
                      color: colors.swiftcarry[100],
                    },
                  },
                }}
              />
              <FormControl
                size="small"
                sx={{
                  minWidth: 110,
                  '& .MuiOutlinedInput-root': {
                    height: '36px',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: colors.swiftcarry[100],
                      borderWidth: '2px',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: colors.swiftcarry[100],
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '13px',
                    '&.Mui-focused': {
                      color: colors.swiftcarry[100],
                    },
                  },
                }}
              >
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => {
                    const newStatusFilter = e.target.value;
                    setStatusFilter(newStatusFilter);
                    setPage(0);
                    const formattedStartDate =
                      startDate && dayjs(startDate).isValid()
                        ? dayjs(startDate).format('YYYY-MM-DD')
                        : null;
                    const formattedEndDate =
                      endDate && dayjs(endDate).isValid()
                        ? dayjs(endDate).format('YYYY-MM-DD')
                        : null;
                    fetchBookings({
                      page: 0,
                      pageSize: rowsPerPage,
                      search: search || '',
                      bookingId: bookingIdFilter || '',
                      mobile: mobileFilter || '',
                      tripId: tripIdFilter || '',
                      shipmentId: shipmentIdFilter || '',
                      status: newStatusFilter,
                      startDate: formattedStartDate,
                      endDate: formattedEndDate,
                      deliveryFilter: deliveryFilter || '',
                    });
                  }}
                  label="Status"
                >
                  <DropdownItem value="">All</DropdownItem>
                  {/* <DropdownItem value="Created">Created</DropdownItem>*/}
                  <DropdownItem value="Assigned">Assigned</DropdownItem>
                  <DropdownItem value="In Transit">In Transit</DropdownItem>
                  <DropdownItem value="Delivered">Delivered</DropdownItem>
                  {/* <DropdownItem value="Canceled">Canceled</DropdownItem>*/}
                  <DropdownItem value="Ready for Pickup">
                    Ready for Pickup
                  </DropdownItem>
                  { /* <DropdownItem value="Incident Reported">
                    Incident Reported
                  </DropdownItem>
                  <DropdownItem value="Notify">Notify</DropdownItem>*/}
                </Select>
              </FormControl>

              <FormControl
                size="small"
                sx={{
                  minWidth: 110,
                  '& .MuiOutlinedInput-root': {
                    height: '36px',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: colors.swiftcarry[100],
                      borderWidth: '2px',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: colors.swiftcarry[100],
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '13px',
                    '&.Mui-focused': {
                      color: colors.swiftcarry[100],
                    },
                  },
                }}
              >
                <InputLabel>Delivery</InputLabel>
                <Select
                  value={deliveryFilter}
                  onChange={(e) => {
                    const newDeliveryFilter = e.target.value;
                    setDeliveryFilter(newDeliveryFilter);
                    setPage(0);
                    const formattedStartDate =
                      startDate && dayjs(startDate).isValid()
                        ? dayjs(startDate).format('YYYY-MM-DD')
                        : null;
                    const formattedEndDate =
                      endDate && dayjs(endDate).isValid()
                        ? dayjs(endDate).format('YYYY-MM-DD')
                        : null;
                    fetchBookings({
                      page: 0,
                      pageSize: rowsPerPage,
                      search: search || '',
                      bookingId: bookingIdFilter || '',
                      mobile: mobileFilter || '',
                      tripId: tripIdFilter || '',
                      shipmentId: shipmentIdFilter || '',
                      status: statusFilter || '',
                      startDate: formattedStartDate,
                      endDate: formattedEndDate,
                      deliveryFilter: newDeliveryFilter,
                    });
                  }}
                  label="Delivery"
                >
                  <DropdownItem value="">All</DropdownItem>
                  <DropdownItem value="pending">🔵 Pending</DropdownItem>
                  <DropdownItem value="on time">🟢 On Time</DropdownItem>
                  <DropdownItem value="exceeded">🔴 Exceeded</DropdownItem>
                </Select>
              </FormControl>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(newDate) => setStartDate(newDate)}
                  format="DD-MM-YYYY"
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: {
                        minWidth: 110,
                        '& .MuiOutlinedInput-root': {
                          height: '36px',
                          borderRadius: '8px',
                          backgroundColor: 'white',
                          '&.Mui-focused fieldset': {
                            borderColor: colors.swiftcarry[100],
                            borderWidth: '2px',
                          },
                          '&:hover fieldset': {
                            borderColor: colors.swiftcarry[100],
                          },
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '13px',
                          '&.Mui-focused': {
                            color: colors.swiftcarry[100],
                          },
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(newDate) => setEndDate(newDate)}
                  format="DD-MM-YYYY"
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: {
                        minWidth: 110,
                        '& .MuiOutlinedInput-root': {
                          height: '36px',
                          borderRadius: '8px',
                          backgroundColor: 'white',
                          '&.Mui-focused fieldset': {
                            borderColor: colors.swiftcarry[100],
                            borderWidth: '2px',
                          },
                          '&:hover fieldset': {
                            borderColor: colors.swiftcarry[100],
                          },
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '13px',
                          '&.Mui-focused': {
                            color: colors.swiftcarry[100],
                          },
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>

              <IconButton
                onClick={handleSearch}
                sx={{
                  bgcolor: colors.swiftcarry[100],
                  color: 'white',
                  width: '50px',
                  height: '50px',
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0, 201, 150, 0.3)',
                  '&:hover': {
                    bgcolor: colors.swiftcarry[100],
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(0, 201, 150, 0.4)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <Search fontSize="medium" />
              </IconButton>

              <IconButton
                onClick={handleReset}
                sx={{
                  border: `2px solid ${colors.swiftcarry[100]}`,
                  color: colors.swiftcarry[100],
                  backgroundColor: 'white',
                  width: '50px',
                  height: '50px',
                  borderRadius: '12px',
                  transition: 'all 0.2s ease',
                  flexShrink: 0,
                  '&:hover': {
                    backgroundColor: `${colors.swiftcarry[100]}10`,
                    transform: 'scale(1.05)',
                  },
                }}
              >
                <Refresh fontSize="medium" />
              </IconButton>
            </Box>

            {isLoading ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '300px',
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer component={Paper} sx={{ mt: 2 }}>
                  <Table sx={{ minWidth: 650 }}>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 'bold', py: 2 }}>
                          Booking Id
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', py: 2 }}>
                          Trip Id
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', py: 2 }}>
                          Shipment Id
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', py: 2 }}>
                          Source
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', py: 2 }}>
                          Destination
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', py: 2 }}>
                          Traveller Name
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', py: 2 }}>
                          Sender Name
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 'bold',
                            py: 2,
                            minWidth: 200,
                            width: 200,
                          }}
                        >
                          Booking Date
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', py: 2 }}>
                          Status
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', py: 2 }}>
                          Action
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center">
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                flexDirection: 'column',
                              }}
                            >
                              <img
                                style={{
                                  maxWidth: '45%',
                                  marginBottom: '16px',
                                }}
                                src={NotFound}
                                alt="Not Found"
                              />
                              <Typography>No data available</Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : (
                        data.map((row) => (
                          <TableRow
                            key={row.id}
                            sx={{
                              backgroundColor: getRowBackgroundColor(row),
                              '&:hover': {
                                backgroundColor: `${getRowBackgroundColor(
                                  row,
                                )}CC`, // Add transparency on hover
                              },
                            }}
                          >
                            <TableCell>{row.bookingId}</TableCell>
                            <TableCell>{row.tripId}</TableCell>
                            <TableCell>{row.shipmentId}</TableCell>
                            <TableCell>{row.source}</TableCell>
                            <TableCell>{row.destination}</TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 0.5,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500, color: '#333' }}
                                >
                                  {row.travelerDetails.name || 'N/A'}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{ color: '#666', fontSize: '0.75rem' }}
                                >
                                  {row.travelerDetails.phone || 'N/A'}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 0.5,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500, color: '#333' }}
                                >
                                  {row.senderDetails.name || 'N/A'}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{ color: '#666', fontSize: '0.75rem' }}
                                >
                                  {row.senderDetails.phone || 'N/A'}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              {formatToIST(row.bookingDateTime)}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={row.status || 'N/A'}
                                color={getStatusStyles(row.status)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <IconButton
                                onClick={(event) => handleMenuOpen(event, row)}
                              >
                                <Settings />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box
                  sx={{
                    mt: 2,
                    borderTop: '1px solid #e0e0e0',
                    backgroundColor: '#fafafa',
                  }}
                >
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50, 100]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{
                      '& .MuiTablePagination-toolbar': {
                        padding: '16px 24px',
                        minHeight: '64px',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      },
                      '& .MuiTablePagination-selectLabel': {
                        marginBottom: 0,
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#666',
                      },
                      '& .MuiTablePagination-displayedRows': {
                        marginBottom: 0,
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#666',
                      },
                      '& .MuiTablePagination-select': {
                        borderRadius: '6px',
                        border: '1px solid #e0e0e0',
                        padding: '4px 8px',
                        marginLeft: '8px',
                        marginRight: '16px',
                        '&:focus': {
                          borderColor: colors.swiftcarry[100],
                          backgroundColor: 'transparent',
                        },
                      },
                      '& .MuiTablePagination-actions': {
                        marginLeft: '20px',
                        '& .MuiIconButton-root': {
                          padding: '8px',
                          margin: '0 2px',
                          borderRadius: '6px',
                          border: '1px solid #e0e0e0',
                          backgroundColor: 'white',
                          '&:hover': {
                            backgroundColor: colors.swiftcarry[100],
                            borderColor: colors.swiftcarry[100],
                            color: 'white',
                          },
                          '&.Mui-disabled': {
                            backgroundColor: '#f5f5f5',
                            borderColor: '#e0e0e0',
                            color: '#ccc',
                          },
                        },
                      },
                    }}
                  />
                </Box>
              </>
            )}

            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={handleMenuClose}
            >
              <DropdownItem
                onClick={() => {
                  handleViewDetails(selectedBooking, selectedBooking.id);
                  handleMenuClose();
                }}
              >
                View
              </DropdownItem>
            </Menu>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Booking;
